import { onControlEventListenersCommandSaga } from 'features/sdk/onControlEventListenersCommandSaga';
import { onControlTileActionSaga } from 'features/sdk/onControlTileActionSaga';
import { onControlUICallbackSaga } from 'features/sdk/onControlUICallbackSaga';
import { SDKConnected } from 'features/sdk/sdkStateSlice';
import { validateSDKInitStateSaga } from 'features/sdk/validateSDKInitStateSaga';
import { call, put, select, take } from 'redux-saga/effects';
import { eventBus, SDKConnectionPayload } from 'utils/eventBus';
import { mapSDKLabelsToDeviceIds } from 'utils/sdk/mapSDKLabelsToDeviceIds';
import { AudioPool } from 'utils/audio/AudioPool';
import { onConfigureVBCommandSaga } from 'features/sdk/onConfigureVBCommandSaga';
import { sdkConfigured } from 'features/sdk/actions';
import { roomValidated, selectRoomValidated } from 'features/room/roomSlice';

export function* onSDKConnectedSaga(rawInitState: SDKConnectionPayload) {
  const initState: SDKConnectionPayload = yield call(validateSDKInitStateSaga, rawInitState);

  yield call(eventBus.onConnection, initState);

  yield put(SDKConnected(initState || {}));

  // make sure we don't process SDK connection before room validation.
  // If validation has already happened we can proceed immediately.
  const alreadyValidated: boolean = yield select(selectRoomValidated);
  if (!alreadyValidated) {
    yield take(roomValidated);
  }

  if (initState.mediaDevices) {
    yield call(mapSDKLabelsToDeviceIds, initState.mediaDevices);
  }

  if (initState.muteFrame) {
    yield call(AudioPool.muteAll);
  } else {
    yield call(AudioPool.unmuteAll);
  }

  if (initState.virtualBackground) {
    yield call(onConfigureVBCommandSaga, initState.virtualBackground, false);
  }

  if (initState.eventListeners?.length) {
    for (const event of initState.eventListeners) {
      yield call(onControlEventListenersCommandSaga, event.operation, {
        eventName: event.event,
        target: event.target,
      });
    }
  }

  if (initState.UICallbacks?.length) {
    for (const callback of initState.UICallbacks) {
      yield call(onControlUICallbackSaga, callback.operation, {
        name: callback.name,
      });
    }
  }

  if (initState.tileActions?.length) {
    for (const action of initState.tileActions) {
      const data = {
        name: action.name,
        properties: action.properties,
      };

      yield call(onControlTileActionSaga, action.operation, data);
    }
  }

  // Ensure this is the last line in this saga to keep the proper setup process
  yield put(sdkConfigured(initState || {}));
}
